/**
 * Activity route (§4.5).
 *
 * MVP scope: unified audit-log timeline with a free-text agent filter.
 * Cross-agent conversations (from `.jsonl` session files on disk) are
 * spec'd as a second view-mode in the plan, but rendering transcripts
 * lives in a follow-up PR — for now a single timeline is plenty.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, Activity as ActivityIcon } from 'lucide-react';
import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/EmptyState';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { GlobalOverlays } from '@/components/GlobalOverlays';
import { formatDate } from '@/lib/format';

interface AuditEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  target?: string;
  success: boolean;
  detail?: string;
}

interface AuditResponse {
  items: AuditEntry[];
  next_cursor?: string;
}

export default function Activity() {
  const [filter, setFilter] = useState('');

  const query = useQuery<AuditResponse>({
    queryKey: ['activity', filter],
    queryFn: () => {
      const qs = filter ? `?target=${encodeURIComponent(filter)}` : '';
      return api<AuditResponse>(`/system/audit-log${qs}`);
    },
    retry: false,
  });

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Activity</h1>
          <p className="text-sm text-muted-foreground">
            Audit log across all agents.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => query.refetch()}>
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      <div>
        <input
          type="text"
          placeholder="Filter by agent or target…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="flex h-9 w-full max-w-md rounded-md border border-input bg-background px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
      </div>

      {query.isLoading && <LoadingSkeleton className="h-60 w-full" />}
      {query.isError && (
        <p className="text-sm text-destructive">
          Could not load activity: {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items.length === 0 && (
        <EmptyState
          icon={ActivityIcon}
          title="No activity"
          description="Mutations will show up here as they happen."
        />
      )}
      {query.data && query.data.items.length > 0 && (
        <ol className="space-y-2" aria-label="Audit log">
          {query.data.items.map((e) => (
            <li key={e.id}>
              <Card className="flex flex-col gap-1 p-4 md:flex-row md:items-center md:gap-6">
                <div className="min-w-[9rem] font-mono text-xs text-muted-foreground">
                  {formatDate(e.timestamp)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-medium">{e.user}</span>
                    <span className="font-mono text-xs text-muted-foreground">{e.action}</span>
                    {e.target && (
                      <span className="text-xs text-muted-foreground">
                        → <code>{e.target}</code>
                      </span>
                    )}
                  </div>
                  {e.detail && (
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.detail}</div>
                  )}
                </div>
                <div
                  className={
                    e.success
                      ? 'text-xs font-medium text-success'
                      : 'text-xs font-medium text-destructive'
                  }
                >
                  {e.success ? 'success' : 'failure'}
                </div>
              </Card>
            </li>
          ))}
        </ol>
      )}
      <GlobalOverlays />
    </div>
  );
}
